// A card for a sample file the reader can take away: a primary-tinted icon
// tile (same as ItemCard), title, one-line description, a length chip, a
// primary Download button and a secondary Preview that opens an inline
// AudioBar for audio (a new tab for anything else), then an optional
// "What to expect" list so the reader can check their result.
//
//   <DownloadCard href="/academy/samples/x.mp3" fileName="x.mp3"
//     title="Sample dictation" description="…" length="1 min 11 s"
//     whatToExpect={["A draft narrative…", "A report score…"]} />
import { useState } from "react";
import { ArrowDownTrayIcon, DocumentIcon, MusicalNoteIcon, PlayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import AudioBar from "./AudioBar";
import Button from "./Button";

const AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|flac|webm)$/i;

/** Saves `href` as `fileName` through a transient <a download>. */
export function downloadFile(href: string, fileName: string) {
  const a = document.createElement("a");
  // Academy clips are served through a signed redirect; ask for an attachment
  // so the browser saves the file instead of playing it.
  a.href = href.includes("/api/academy/assets/") ? `${href}${href.includes("?") ? "&" : "?"}download=1` : href;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

interface Props {
  href: string;
  /** Saved-as name (the `download` attribute). */
  fileName: string;
  title: string;
  description?: string;
  /** "1 min 11 s", "2 pages". */
  length?: string;
  whatToExpect?: string[];
  className?: string;
}

const DownloadCard: React.FC<Props> = ({ href, fileName, title, description, length, whatToExpect, className }) => {
  const [previewing, setPreviewing] = useState(false);
  const isAudio = AUDIO_EXT.test(fileName) || AUDIO_EXT.test(href);
  const Icon = isAudio ? MusicalNoteIcon : DocumentIcon;

  const download = () => downloadFile(href, fileName);

  const preview = () => {
    if (isAudio) setPreviewing((v) => !v);
    else window.open(href, "_blank", "noopener");
  };

  return (
    <div className={cn("rounded-lg border border-base-300 bg-base-100 p-4", className)}>
      <div className="flex items-start gap-3">
        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-medium text-sm m-0">{title}</h3>
              {description && <p className="text-xs text-base-content/60 mt-0.5 m-0">{description}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="secondary" size="sm" onClick={preview} aria-pressed={isAudio ? previewing : undefined}>
                {previewing ? <XMarkIcon className="size-4" /> : <PlayIcon className="size-4" />}
                {previewing ? "Close" : "Preview"}
              </Button>
              <Button variant="primary" size="sm" onClick={download}>
                <ArrowDownTrayIcon className="size-4" /> Download
              </Button>
            </div>
          </div>
          {length && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 rounded-full bg-base-300 text-base-content/60">{length}</span>
              <span className="text-[11px] text-base-content/50 truncate">{fileName}</span>
            </div>
          )}
        </div>
      </div>
      {isAudio && previewing && (
        <div className="mt-3 rounded-lg border border-base-300 bg-base-200/50">
          <AudioBar src={href} />
        </div>
      )}
      {whatToExpect && whatToExpect.length > 0 && (
        <div className="mt-4 border-t border-base-200 dark:border-base-300 pt-3">
          <p className="m-0 text-xs font-semibold uppercase tracking-wider text-base-content/60">What to expect</p>
          <ul className="mt-2 space-y-1.5 pl-4 list-disc marker:text-blue-500 text-sm text-base-content/80">
            {whatToExpect.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DownloadCard;
